// src/services/captureLimitsService.ts
import { logger } from '../utils/logger';



import { supabase } from './supabase';



const CAPTURE_LIMIT_FREE = 5;

const CAPTURE_LIMIT_PREMIUM = -1; // unlimited

const RESET_INTERVAL_MS = 60 * 60 * 1000; // 1 hour



interface CaptureAttempt {

  timestamp: number;

}



interface CaptureLimitsData {

  remaining: number;

  total: number;

  resetTime: number;

  attempts: CaptureAttempt[];

}



/**

 * Get the storage key for capture limits

 */

const getStorageKey = (userId: string): string => {

  return `capture_limits_${userId}`;

};



/**

 * Load capture limits from localStorage

 */

const loadLimits = (userId: string): CaptureLimitsData | null => {

  try {

    const stored = localStorage.getItem(getStorageKey(userId));

    if (!stored) return null;



    const data = JSON.parse(stored);

    return data;

  } catch (error) {

    logger.error('Error loading capture limits:', error);

    return null;

  }

};



/**

 * Save capture limits to localStorage

 */

const saveLimits = (userId: string, data: CaptureLimitsData): void => {

  try {

    localStorage.setItem(getStorageKey(userId), JSON.stringify(data));

  } catch (error) {

    logger.error('Error saving capture limits:', error);

  }

};



/**

 * Initialize capture limits for a user

 */

const initializeLimits = (userId: string, isPremium: boolean): CaptureLimitsData => {

  const now = Date.now();

  const data: CaptureLimitsData = {

    remaining: isPremium ? CAPTURE_LIMIT_PREMIUM : CAPTURE_LIMIT_FREE,

    total: isPremium ? CAPTURE_LIMIT_PREMIUM : CAPTURE_LIMIT_FREE,

    resetTime: now + RESET_INTERVAL_MS,

    attempts: []

  };



  saveLimits(userId, data);

  return data;

};



/**

 * Clean up old attempts (older than reset interval)

 */

const cleanupOldAttempts = (attempts: CaptureAttempt[], now: number): CaptureAttempt[] => {

  const cutoff = now - RESET_INTERVAL_MS;

  return attempts.filter(attempt => attempt.timestamp > cutoff);

};



/**

 * Check if user can make a capture attempt

 */

export const canAttemptCapture = async (): Promise<{

  allowed: boolean;

  remaining: number;

  resetTime: number;

  isPremium: boolean;

  reason?: string;

}> => {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {

      return {

        allowed: false,

        remaining: 0,

        resetTime: Date.now(),

        isPremium: false,

        reason: 'Not authenticated'

      };

    }



    // Check premium status

    const { data: subscription } = await supabase

      .from('subscriptions')

      .select('is_premium, premium_expires_at, subscription_type')

      .eq('user_id', user.id)

      .single();



    const isLifetime = subscription?.subscription_type === 'lifetime';

    const notExpired = isLifetime || !subscription?.premium_expires_at ||

      new Date(subscription.premium_expires_at) > new Date();

    const isPremium = subscription?.is_premium === true && notExpired;



    // Premium users have unlimited attempts

    if (isPremium) {

      return {

        allowed: true,

        remaining: -1,

        resetTime: Date.now(),

        isPremium: true

      };

    }



    // Free users - check limits

    const now = Date.now();

    let limits = loadLimits(user.id);



    // Initialize if no data exists

    if (!limits) {

      limits = initializeLimits(user.id, false);

    }



    // Clean up old attempts

    limits.attempts = cleanupOldAttempts(limits.attempts, now);



    // Check if reset time has passed

    if (now >= limits.resetTime) {

      // Reset the counter

      limits = initializeLimits(user.id, false);

    }



    // Calculate remaining attempts

    const used = limits.attempts.length;

    const remaining = CAPTURE_LIMIT_FREE - used;



    if (remaining <= 0) {

      const minutesUntilReset = Math.ceil((limits.resetTime - now) / (60 * 1000));

      return {

        allowed: false,

        remaining: 0,

        resetTime: limits.resetTime,

        isPremium: false,

        reason: `You've used all ${CAPTURE_LIMIT_FREE} capture attempts. Resets in ${minutesUntilReset} minutes.`

      };

    }



    return {

      allowed: true,

      remaining,

      resetTime: limits.resetTime,

      isPremium: false

    };

  } catch (error) {

    logger.error('Error checking capture limits:', error);

    return {

      allowed: false,

      remaining: 0,

      resetTime: Date.now(),

      isPremium: false,

      reason: 'Error checking limits'

    };

  }

};



/**

 * Record a capture attempt

 */

export const recordCaptureAttempt = async (): Promise<void> => {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;



    const now = Date.now();

    let limits = loadLimits(user.id);



    if (!limits) {

      limits = initializeLimits(user.id, false);

    }



    // Clean up old attempts

    limits.attempts = cleanupOldAttempts(limits.attempts, now);



    // Add new attempt

    limits.attempts.push({ timestamp: now });



    // Update remaining

    limits.remaining = Math.max(0, CAPTURE_LIMIT_FREE - limits.attempts.length);



    saveLimits(user.id, limits);

  } catch (error) {

    logger.error('Error recording capture attempt:', error);

  }

};



/**

 * Get time until next reset in a human-readable format

 */

export const getTimeUntilReset = (resetTime: number): string => {

  const now = Date.now();

  const diff = resetTime - now;



  if (diff <= 0) return 'Ready to reset';



  const minutes = Math.floor(diff / (60 * 1000));

  const hours = Math.floor(minutes / 60);

  const remainingMinutes = minutes % 60;



  if (hours > 0) {

    return `${hours}h ${remainingMinutes}m`;

  }

  return `${minutes}m`;

};



/**

 * Reset limits (for testing or admin)

 */

export const resetCaptureLimits = async (): Promise<void> => {

  try {

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;



    localStorage.removeItem(getStorageKey(user.id));

    logger.log('Capture limits reset');

  } catch (error) {

    logger.error('Error resetting capture limits:', error);

  }

};